// Pure voice-intent matchers — extracted from voiceCommands.ts so they carry
// no DOM/Web-Speech imports and can be unit-tested directly (tests/voice-match.test.ts).
//
// All matching is accent-insensitive: pt-BR speech-to-text returns accented
// text ("estações", "suíte", "recepção") but room ids are usually unaccented
// ("estacoes", "suite", "recepcao"). normalizePt() strips diacritics on BOTH
// sides so "estações" reaches room id "estacoes" instead of silently falling
// through. This is what lets every room in every demo be reached by its short
// spoken form, not just its full name.

import type { Room } from '../app/types';

/** Lowercase + strip diacritics (NFD decompose, drop combining marks). */
export function normalizePt(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // drop combining diacritical marks
    .toLowerCase()
    .trim();
}

/**
 * Resolve a spoken transcript to a room. Tries, in order:
 *   1. exact (accent-folded) room-name substring — works for any demo
 *   2. room-id token match ("corredor_predio" → corredor / predio)
 *   3. pt-BR/EN synonym stems → candidate ids (first existing wins)
 * Returns null when nothing matches.
 */
export function matchesRoom(text: string, rooms: Room[]): Room | null {
  const ntext = normalizePt(text);

  // 1) Accent-folded room-name substring.
  for (const room of rooms) {
    if (ntext.includes(normalizePt(room.name))) return room;
  }
  // 2) Room-id token match — tokens ≥3 chars, accent-folded.
  for (const room of rooms) {
    const tokens = normalizePt(room.id)
      .split(/[_\-\s]+/)
      .filter((t) => t.length >= 3);
    if (tokens.some((t) => new RegExp(`\\b${t}`).test(ntext))) return room;
  }
  // 3) Synonym stems → candidate ids. Patterns are already accent-folded
  //    (and keep accented variants harmlessly) so they test cleanly on ntext.
  const synonyms: Array<[RegExp, string[]]> = [
    [/\b(sala|living|estar)\b/, ['sala', 'living_room']],
    [/\b(cozinha|kitchen)\b/, ['cozinha', 'kitchen']],
    [/\b(banheiro|lavabo|toalete|bathroom|wc)\b/, ['banheiro', 'bathroom']],
    [/\b(suite)\b/, ['suite']],
    [/\b(quarto|dormitorio|bedroom)\b/, ['quarto', 'bedroom', 'suite']],
    [/\b(entrada|hall|foyer|entrance)\b/, ['entrada']],
    [/\b(corredor|hallway|corridor)\b/, ['corredor', 'corredor_predio']],
    [/\b(recepcao|reception|lobby)\b/, ['recepcao']],
    [/\b(estacoes|estacao|workstation|workstations)\b/, ['estacoes']],
    [/\b(workspace|escritorio|office)\b/, ['workspace']],
  ];
  for (const [re, ids] of synonyms) {
    if (re.test(ntext)) {
      const r = rooms.find((x) => ids.includes(x.id));
      if (r) return r;
    }
  }
  return null;
}

/**
 * Words that mean "tell me about this", in both languages.
 *
 * Exported so voiceCommands can distinguish "what is that sofa" (open the sofa
 * hotspot) from a bare "informações" (list what this room offers). In VR this
 * is the only way to reach an info hotspot at all: the HTML labels are not
 * composited into an immersive session, so there is nothing to look at and
 * click — the picker path that works on a phone does not exist in a headset.
 */
export const INFO_INTENT =
  /\b(informacoes|informacao|info|detalhes|detalhe|sobre|o que e|que e isso|me fale|fale sobre|conte sobre|descreva|information|details|about|what is|what's|tell me|describe)\b/;

/**
 * Resolve a transcript to an info hotspot in the given list.
 *
 * Matches on the hotspot's own label, accent-folded, so it follows whatever
 * language the config was localised into — and because `localizeConfig`
 * resolves in place before any consumer runs, that is simply the active
 * locale's label with no extra plumbing here.
 *
 * The label is checked as a substring of the transcript rather than the other
 * way round: people say "me fale sobre o sofá de bouclé" for a hotspot labelled
 * "Sofá", and requiring the whole utterance to be the label would reject every
 * natural phrasing.
 */
export function matchesInfoHotspot<T extends { type: string; label: string }>(
  text: string,
  hotspots: readonly T[],
): Extract<T, { type: 'info' }> | null {
  const ntext = normalizePt(text);
  const isInfo = (h: T): h is Extract<T, { type: 'info' }> => h.type === 'info';
  const infos = hotspots.filter(isInfo);
  // Longest label first: a room with both "Sofá" and "Sofá de canto" must not
  // resolve the more specific phrase to the shorter hotspot.
  const byLength = [...infos].sort((a, b) => b.label.length - a.label.length);
  for (const hotspot of byLength) {
    const label = normalizePt(hotspot.label);
    if (label.length >= 3 && ntext.includes(label)) return hotspot;
  }
  return null;
}

/** True when the transcript names this variant (by accent-folded name or a preset stem). */
export function matchesVariant(text: string, name: string, id: string): boolean {
  const ntext = normalizePt(text);
  const cleanName = normalizePt(name).replace(/^antes |^ia · |^gerado · |^repintado · /i, '');
  if (cleanName && ntext.includes(cleanName)) return true;
  // Keyword stems for the canonical presets (accent-folded).
  const stems: Record<string, RegExp> = {
    warm_luxury: /\b(conforto quente|warm|quente|conforto)\b/,
    minimal_white: /\b(minimal|minimalist|minimalismo|branco|white)\b/,
    dark_premium: /\b(dark premium|dark|escuro|premium)\b/,
    biofilico: /\b(biofilico|biophilic|plantas)\b/,
    modernista_brasileiro: /\b(modernist|modernismo|niemeyer|brasileiro)\b/,
    before: /\b(antes|original|estado atual)\b/,
  };
  const re = stems[id];
  return !!(re && re.test(ntext));
}
